/**
 * Active while settings.show_milestone_bar_v2 is on (the theme then renders
 * snippets/cart-milestone-rewards.liquid and scripts.liquid loads this
 * file). This replaced an earlier discount-code-based implementation.
 *
 * Claims the Hair Serum reward by adding a dedicated, permanently-priced
 * reward VARIANT to the cart (no discount code, so nothing to lose a
 * combination fight with a Buy X Get Y / bundle discount elsewhere in the
 * cart — see the doc block in the .liquid file for why a discount code
 * couldn't just fix that in Liquid). Nothing is added without the shopper
 * clicking "Claim Offer" (data-cart-milestone-v2-claim) — same click-to-claim
 * convention as the free trial gift. Once claimed, the line shows a "Remove
 * Claim" button (data-cart-milestone-v2-remove, wired in
 * snippets/cart-products.liquid) instead of the normal quantity stepper —
 * clicking it withdraws the line and brings the claim card back. Unlike the
 * free trial gift, which keeps its line once claimed, this reward's line is
 * also fully withdrawn automatically the moment the qualifying subtotal
 * drops back below the threshold — a deliberate, stricter choice for this
 * offer.
 *
 * All eligibility reasoning lives in snippets/cart-milestone-rewards.liquid,
 * which renders a hidden state node describing what the cart currently holds;
 * this file only reads that description and, when the cart disagrees with the
 * rule, corrects it — same split as assets/cart-trial.js.
 *
 * State node contract (snippets/cart-milestone-rewards.liquid):
 *   data-qualifying         sum of every non-reward line's final_line_price
 *   data-tier2-threshold    cents; reward threshold
 *   data-reward-variant-id  the reward variant's id
 *   data-reward-qty         reward units currently in the cart
 *   data-reward-keys        every reward line's key, comma-joined (normally
 *                           just one, but merges conservatively like
 *                           cart-trial.js in case of a stale morph)
 *
 * Why line key rather than variant id: a variant can occupy several lines when
 * line item properties differ, and a numeric id addressed to /cart/change.js or
 * /cart/update.js silently targets a line index instead. The key is unambiguous.
 */
(() => {
  if (window.__cartMilestoneV2Init) return;
  window.__cartMilestoneV2Init = true;

  /** A runaway render -> correct -> render loop would hammer the cart API. */
  const MAX_CORRECTIONS = 4;

  let inFlight = false;
  let corrections = 0;
  let scheduled = null;

  const stateNodes = () => Array.from(document.querySelectorAll('[data-cart-milestone-v2-state]'));

  /**
   * Merge every state node's reading conservatively, mirroring cart-trial.js's
   * readState(): qualifying takes the SMALLEST reported value (never assume
   * more paid items are in the bag than the most cautious node shows), reward
   * qty/keys are taken together from whichever node reports the LARGEST
   * quantity (the most complete view of what is actually in the cart).
   */
  function readState() {
    const nodes = stateNodes();
    if (!nodes.length) return null;

    let qualifying = Infinity;
    let tier2 = 0;
    let variantId = '';
    let qty = 0;
    let keys = [];

    for (const node of nodes) {
      qualifying = Math.min(qualifying, Number(node.dataset.qualifying) || 0);
      tier2 = Number(node.dataset.tier2Threshold) || tier2;
      if (node.dataset.rewardVariantId) variantId = node.dataset.rewardVariantId;

      const nodeQty = Number(node.dataset.rewardQty) || 0;
      if (nodeQty > qty) {
        qty = nodeQty;
        keys = (node.dataset.rewardKeys || '').split(',').filter(Boolean);
      }
    }

    return {
      qualifying: qualifying === Infinity ? 0 : qualifying,
      tier2,
      variantId,
      qty,
      keys,
    };
  }

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

  function broadcast(data, source) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: source,
          data: {
            itemCount: data.item_count,
            sections: data.sections || {},
            source,
          },
        },
      })
    );
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) {
      throw new Error(`cart-milestone-rewards-v2: ${url} responded ${response.status}`);
    }
    return data;
  }

  /**
   * The `_milestone_reward` property (leading underscore hides it from the
   * cart UI and order confirmation) lets the Liquid tell this line apart from
   * any other line that might land on the same variant id — belt and braces
   * alongside the variant being dedicated/unpublished.
   */
  function addRewardLine(variantId) {
    const body = { id: Number(variantId), quantity: 1, properties: { _milestone_reward: 'true' } };
    const ids = sectionIds();
    if (ids.length) body.sections = ids;
    return postJSON('/cart/add.js', body);
  }

  /** @param {Record<string, number>} updates - line key -> target quantity */
  function applyUpdates(updates) {
    const body = { updates };
    const ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }
    return postJSON('/cart/update.js', body);
  }

  /**
   * Bring the cart back in line with the rule:
   *   - if the qualifying subtotal has dropped below the threshold, withdraw
   *     every reward line outright.
   *   - otherwise, cap the reward at exactly one unit total, however it got
   *     into the cart. The reward variant's product page is public (required
   *     for /cart/add.js to work at all — see the .liquid doc block), so a
   *     shopper can add it there too, untagged, possibly at a quantity above
   *     1. readState() lists a tagged (claimed) key before any untagged one,
   *     so keeping "the first key" here prefers the properly-claimed line.
   * Returns quietly when there is nothing to do, which is the common case.
   * Claiming the FIRST unit is never automatic — see the click handlers below.
   */
  async function reconcile() {
    if (inFlight) return;

    const state = readState();
    if (!state) return;

    const { qualifying, tier2, qty, keys } = state;

    if (!keys.length || qty === 0) {
      corrections = 0;
      return;
    }

    /** @type {Record<string, number> | null} */
    let updates = null;
    if (qualifying < tier2) {
      updates = Object.fromEntries(keys.map((key) => [key, 0]));
    } else if (qty > 1 || keys.length > 1) {
      updates = Object.fromEntries(keys.map((key, i) => [key, i === 0 ? 1 : 0]));
    }

    if (updates === null) {
      corrections = 0;
      return;
    }

    if (corrections >= MAX_CORRECTIONS) {
      console.warn('cart-milestone-rewards-v2: giving up after %d corrections', corrections);
      return;
    }

    corrections += 1;
    inFlight = true;
    try {
      const data = await applyUpdates(updates);
      broadcast(data, 'cart-milestone-v2-reconcile');
    } catch (error) {
      console.warn('cart-milestone-rewards-v2: could not correct the reward line(s)', error);
    } finally {
      inFlight = false;
    }
  }

  function scheduleReconcile() {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      reconcile();
    }, 60);
  }

  // ── Claim button ────────────────────────────────────────────────────────
  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-milestone-v2-claim]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (inFlight) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = '…';
    inFlight = true;

    try {
      const data = await addRewardLine(variantId);
      button.textContent = '✓';
      corrections = 0;
      broadcast(data, 'cart-milestone-v2-claim');
    } catch (error) {
      console.warn('cart-milestone-rewards-v2: could not claim the reward', error);
      button.textContent = original;
      button.disabled = false;
    } finally {
      inFlight = false;
    }
  });

  // ── Remove Claim button ─────────────────────────────────────────────────
  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-milestone-v2-remove]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (inFlight) return;

    const key = button.dataset.key;
    if (!key) return;

    button.disabled = true;
    inFlight = true;

    try {
      const data = await applyUpdates({ [key]: 0 });
      corrections = 0;
      broadcast(data, 'cart-milestone-v2-unclaim');
    } catch (error) {
      console.warn('cart-milestone-rewards-v2: could not remove the claimed reward', error);
      button.disabled = false;
    } finally {
      inFlight = false;
    }
  });

  // ── Keep the cart honest ────────────────────────────────────────────────
  document.addEventListener('cart:update', scheduleReconcile);

  /**
   * Bring this feature's state fully in line with the rule right now,
   * bypassing scheduleReconcile()'s 60ms debounce. Used by the checkout
   * guard below so a shopper who fires checkout before that timer would
   * have run never reaches checkout with a stale reward line.
   */
  async function flush() {
    if (scheduled) {
      clearTimeout(scheduled);
      scheduled = null;
    }
    await reconcile();
    // reconcile()'s own broadcast() re-triggers scheduleReconcile via the
    // cart:update listener above; that follow-up is redundant once we've
    // already awaited a fresh pass, so cancel it and confirm settled.
    if (scheduled) {
      clearTimeout(scheduled);
      scheduled = null;
    }
    await reconcile();
  }

  // ── Checkout guard ───────────────────────────────────────────────────────
  // A shopper who removed a qualifying item and hit checkout fast used to
  // reach checkout with the reward line still sitting there: reconcile()
  // only runs after a debounce, and even that debounce starts from
  // cart:update, which itself only fires once component-cart-items.js's own
  // (separately debounced) /cart/change.js request has finished. Block the
  // checkout button until the cart is provably quiet, then flush.
  //
  // The listener below is shared with assets/cart-trial.js via the same
  // window-flag idiom as window.__cartMilestoneV2Init above: whichever of
  // the two loads first installs the one checkout-button listener, and
  // every reconcile-based offer just registers a flush() into the queue.
  if (!window.__cartCheckoutGuardFlushers) window.__cartCheckoutGuardFlushers = [];
  window.__cartCheckoutGuardFlushers.push(flush);

  if (!window.__cartCheckoutGuardInit) {
    window.__cartCheckoutGuardInit = true;

    let quantityChangePending = false;
    let quantitySettleTimer = null;

    // component-cart-items.js debounces quantity-selector:update 300ms
    // before it even starts the /cart/change.js request, so a checkout
    // click can beat the request out of the gate entirely. Treat the cart
    // as busy from the moment the shopper moves the stepper until
    // cart:update confirms the resulting request landed — with a generous
    // fallback timeout so a request that never lands (offline, blocked)
    // can never wedge checkout shut.
    document.addEventListener('quantity-selector:update', () => {
      quantityChangePending = true;
      if (quantitySettleTimer) clearTimeout(quantitySettleTimer);
      quantitySettleTimer = setTimeout(() => {
        quantityChangePending = false;
      }, 2000);
    });
    document.addEventListener('cart:update', () => {
      quantityChangePending = false;
      if (quantitySettleTimer) clearTimeout(quantitySettleTimer);
    });

    const cartBusy = () =>
      quantityChangePending || !!document.querySelector('cart-items-component.cart-items-disabled');

    let resubmitting = false;

    // Capture phase, ahead of every bubble-phase checkout listener (the
    // cart-discount.js redirect included) and native form submission, so
    // this is the first thing to see the click.
    document.addEventListener(
      'click',
      (event) => {
        if (resubmitting || event.defaultPrevented) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('#checkout, [name="checkout"]');
        if (!(button instanceof HTMLElement)) return;
        if (button instanceof HTMLButtonElement && button.disabled) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        (async () => {
          const originalDisabled = button instanceof HTMLButtonElement ? button.disabled : false;
          if (button instanceof HTMLButtonElement) button.disabled = true;

          for (let waits = 0; waits < 60 && cartBusy(); waits++) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }

          for (const flushOne of window.__cartCheckoutGuardFlushers) {
            try {
              await flushOne();
            } catch (error) {
              console.warn('cart-checkout-guard: a flush failed', error);
            }
          }

          if (button instanceof HTMLButtonElement) button.disabled = originalDisabled;
          resubmitting = true;
          button.click();
          resubmitting = false;
        })();
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }
})();
